// DmpPublishTasksList.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  DataGridPro,
  GridColDef,
  GridColumns,
  GridRenderCellParams,
  GridToolbarContainer,
} from '@mui/x-data-grid-pro'
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Button,
  debounce,
  TextField,
  SelectChangeEvent,
} from '@mui/material'
import {
  TaskNameEnum,
  TaskStatus,
  BackgroundTask,
  DmpEnum,
} from '@services/backgroundTaskService'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import moment, { Moment } from 'moment'
import {
  cancelBackgroundTask,
  doReleaseHeldBackgroundTasks,
  getBackgroundTasks,
} from '@state/thunks/backgroundTaskThunk'
import {
  CancelScheduleSend,
  LockOpen,
  PlayArrow,
  Refresh,
} from '@mui/icons-material'
import { enqueueSnackbar } from 'notistack'
import { ReleaseHeldDmpTask } from './ReleaseHeldDmpTask'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

type ChipColor =
  | 'default'
  | 'primary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'

const STATUS_COLORS: Record<string, ChipColor> = {
  pending: 'default',
  scheduled: 'info',
  inprogress: 'primary',
  completed: 'success',
  failed: 'error',
  cancelled: 'error',
  hold: 'warning',
}

const DMPSTATUS_COLORS: Record<DmpEnum, ChipColor> = {
  [DmpEnum.APPROVED]: 'success',
  [DmpEnum.SUBMITTED]: 'info',
  [DmpEnum.DEPRECATED]: 'warning',
  [DmpEnum.ARCHIVED]: 'default',
  [DmpEnum.DELETED]: 'error',
  [DmpEnum.FAILED]: 'error',
}

function CustomToolbar({ onRefresh }: { onRefresh: () => void }) {
  return (
    <GridToolbarContainer
      sx={{
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Typography variant="h6" sx={{ mr: 2 }}>
          DMP Publications
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={onRefresh}
          size="small"
        >
          Refresh
        </Button>
      </Box>
    </GridToolbarContainer>
  )
}

export default function DmpPublishTasks() {
  const dispatch = useAppDispatch()

  const { items, totalCount, page, pageSize, loading } = useAppSelector(
    (state) => state.backgroundTasks,
  )

  const [statusFilter, setStatusFilter] = useState<TaskStatus | ''>('')
  const [dmpStatusFilter, setDmpStatusFilter] = useState<DmpEnum | ''>('')
  const [patientNameInput, setPatientNameInput] = useState('')
  const [patientNameFilter, setPatientNameFilter] = useState('')

  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false)
  const [selectedTaskUid, setSelectedTaskUid] = useState<string | null>(null)

  const [dateFrom, setDateFrom] = useState<Moment | null>(null)
  const [dateTo, setDateTo] = useState<Moment | null>(null)

  const fetchTasks = useCallback(
    (params?: {
      page?: number
      pageSize?: number
      status?: TaskStatus | ''
      dmpStatus?: DmpEnum | ''
      patientName?: string
      dateFrom?: Moment | null
      dateTo?: Moment | null
    }) => {
      const from = params?.dateFrom !== undefined ? params.dateFrom : dateFrom
      const to = params?.dateTo !== undefined ? params.dateTo : dateTo
      dispatch(
        getBackgroundTasks({
          taskName: TaskNameEnum.DmpPublishTask,
          page: params?.page ?? page,
          pageSize: params?.pageSize ?? pageSize,
          status: (params?.status ?? statusFilter) || undefined,
          dmpStatus: (params?.dmpStatus ?? dmpStatusFilter) || undefined,
          search: (params?.patientName ?? patientNameFilter) || undefined,
          dateFrom: from ? from.toISOString() : undefined,
          dateTo: to ? to.endOf('day').toISOString() : undefined,
        }),
      )
    },
    [
      dispatch,
      page,
      pageSize,
      statusFilter,
      dmpStatusFilter,
      patientNameFilter,
      dateFrom,
      dateTo,
    ],
  )

  const debouncedSetPatientName = useMemo(
    () =>
      debounce((value: string) => {
        const trimmed = value.trim()
        setPatientNameFilter(trimmed)
        fetchTasks({ page: 1, patientName: trimmed })
      }, 500),
    [fetchTasks],
  )

  const handleConfirmRelease = async (delayInMs: number) => {
    if (!selectedTaskUid) return
    await dispatch(
      doReleaseHeldBackgroundTasks({
        uid: selectedTaskUid,
        body: { delayInMs },
      }),
    ).unwrap()
  }

  const handlePatientNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPatientNameInput(e.target.value)
    debouncedSetPatientName(e.target.value)
  }
  const handleDmpStatusFilterChange = (e: SelectChangeEvent) => {
    const value = e.target.value as DmpEnum | ''
    setDmpStatusFilter(value)
    fetchTasks({ page: 1, dmpStatus: value })
  }
  const handleDateFromChange = (value: Moment | null) => {
    setDateFrom(value)
    fetchTasks({ page: 1, dateFrom: value })
  }

  const handleDateToChange = (value: Moment | null) => {
    setDateTo(value)
    fetchTasks({ page: 1, dateTo: value })
  }
  useEffect(() => {
    return () => debouncedSetPatientName.clear()
  }, [debouncedSetPatientName])

  useEffect(() => {
    fetchTasks()
  }, [])

  const handleOpenRelease = (uid: string) => {
    setSelectedTaskUid(uid)
    setReleaseDialogOpen(true)
  }

  const handleStatusFilterChange = (e: SelectChangeEvent) => {
    const value = e.target.value as TaskStatus | ''
    setStatusFilter(value)
    fetchTasks({ page: 1, status: value })
  }

  const handleClearFilters = () => {
    debouncedSetPatientName.clear()
    setStatusFilter('')
    setDmpStatusFilter('')
    setPatientNameInput('')
    setPatientNameFilter('')
    setDateFrom(null)
    setDateTo(null)
    fetchTasks({
      page: 1,
      status: '',
      dmpStatus: '',
      patientName: '',
      dateFrom: null,
      dateTo: null,
    })
  }

  type ActionHandler = 'cancel' | 'release'

  const ACTIONS_BY_STATUS: Partial<
    Record<
      TaskStatus,
      Array<{ icon: React.ReactNode; tooltip: string; handler: ActionHandler }>
    >
  > = {
    [TaskStatus.Scheduled]: [
      { icon: <CancelScheduleSend />, tooltip: 'Annuler', handler: 'cancel' },
    ],
    [TaskStatus.Hold]: [
      { icon: <LockOpen />, tooltip: 'Libérer', handler: 'release' },
      { icon: <CancelScheduleSend />, tooltip: 'Annuler', handler: 'cancel' },
    ],
  }

  const handleCancel = async (uid: string) => {
    try {
      await dispatch(cancelBackgroundTask(uid)).unwrap()
      enqueueSnackbar('Task cancelled', { variant: 'success' })
    } catch {
      enqueueSnackbar('Failed to cancel task', { variant: 'error' })
    }
  }

  const handlers: Record<ActionHandler, (uid: string) => void> = {
    cancel: handleCancel,
    release: handleOpenRelease,
  }

  const columns: GridColumns = [
    {
      field: 'status',
      headerName: 'Statut de la tâche',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={STATUS_COLORS[String(params.value).toLowerCase()] ?? 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'dmpStatus',
      headerName: 'Statut DMP',
      flex: 1,
      minWidth: 130,
      valueGetter: (params) => params.row.dmpSubmissions?.status ?? null,
      renderCell: (params) =>
        params.value ? (
          <Chip
            label={params.value}
            color={DMPSTATUS_COLORS[params.value as DmpEnum] ?? 'default'}
            size="small"
            variant="outlined"
          />
        ) : (
          <>-</>
        ),
    },
    {
      field: 'patient',
      headerName: 'patient(e)',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => {
        const p = params.row.medicalOrder?.patient
        return p
          ? `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || '-'
          : '-'
      },
    },
    {
      field: 'site',
      headerName: 'Site',
      flex: 1,
      minWidth: 100,
      valueGetter: (params) => params.row.site?.label ?? '-',
    },
    // {
    //   field: 'documents',
    //   headerName: 'Documents',
    //   width: 80,
    //   align: 'center',
    //   headerAlign: 'center',
    //   valueGetter: (params) =>
    //     params.row.dmpSubmissions?.documents?.length ?? 0,
    // },
    {
      field: 'reports',
      headerName: 'Rapports',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params) => params.row.payload?.reportIds?.length ?? 0,
    },
    {
      field: 'createdAt',
      headerName: 'Créé',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) =>
        params.row.createdAt
          ? moment(params.row.createdAt).format('DD/MM/YYYY HH:mm')
          : '-',
    },
    {
      field: 'scheduledAt',
      headerName: 'Prévu à',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) =>
        params.row.payload?.scheduledAt
          ? moment(params.row.payload.scheduledAt).format('DD/MM/YYYY HH:mm')
          : '-',
    },
    {
      field: 'completedAt',
      headerName: 'terminé',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) =>
        params.row.completedAt
          ? moment(params.row.completedAt).format('DD/MM/YYYY HH:mm')
          : '-',
    },
    {
      field: 'error',
      headerName: 'Erreur',
      flex: 2,
      minWidth: 220,
      valueGetter: (params) =>
        (params.row as BackgroundTask).error ||
        params.row.dmpSubmissions?.error ||
        '',
      renderCell: (params) =>
        params.value ? (
          <Tooltip title={params.value}>
            <Typography variant="body2" color="error" noWrap>
              {params.value}
            </Typography>
          </Tooltip>
        ) : (
          <>-</>
        ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      align: 'right',
      renderCell: (params: GridRenderCellParams) => {
        const actions = ACTIONS_BY_STATUS[params.row.status as TaskStatus]
        if (!actions?.length) return null

        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {actions.map((a, i) => (
              <Tooltip key={i} title={a.tooltip}>
                <IconButton
                  size="small"
                  onClick={() => handlers[a.handler](params.row.uid)}
                >
                  {a.icon}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        )
      },
    },
  ]

  const statusOptions = Object.values(TaskStatus).map((value) => ({
    label: value,
    value,
  }))

  const statusDmp = Object.values(DmpEnum).map((value) => ({
    label: value,
    value,
  }))

  return (
    <Box
      sx={{
        height: 'calc(100vh - 130px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Paper
        elevation={1}
        sx={{
          p: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Statut</InputLabel>
            <Select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              {statusOptions.map((s, i) => (
                <MenuItem key={`${s.value}/${i}`} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Statut DMP</InputLabel>
            <Select
              value={dmpStatusFilter}
              onChange={handleDmpStatusFilterChange}
              label="DMP Status"
            >
              <MenuItem value="">All</MenuItem>
              {statusDmp.map((s, i) => (
                <MenuItem key={`${s.value}/${i}`} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Patient name"
            value={patientNameInput}
            onChange={handlePatientNameChange}
            sx={{ minWidth: 200 }}
          />
          <DatePicker
            label="à partir du"
            value={dateFrom}
            onChange={handleDateFromChange}
            slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
          />
          <DatePicker
            label="Au"
            value={dateTo}
            onChange={handleDateToChange}
            slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
          />

          <Button variant="outlined" onClick={handleClearFilters} size="small">
            Clear Filters
          </Button>

          <Box sx={{ ml: 'auto' }}>
            <Typography variant="body2" color="text.secondary">
              Total: {totalCount} task(s)
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Paper
        elevation={1}
        sx={{
          flex: 1,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DataGridPro
          rows={items}
          columns={columns}
          getRowId={(row) => row.uid}
          loading={loading}
          disableSelectionOnClick
          pagination
          paginationMode="server"
          page={page - 1}
          pageSize={pageSize}
          rowCount={totalCount}
          onPageChange={(newPage) => fetchTasks({ page: newPage + 1 })}
          onPageSizeChange={(newPageSize) =>
            fetchTasks({ page: 1, pageSize: newPageSize })
          }
          initialState={{
            pinnedColumns: { right: ['actions'] },
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          components={{
            Toolbar: CustomToolbar,
          }}
          componentsProps={{
            toolbar: {
              onRefresh: () => fetchTasks(),
            },
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        />
        <ReleaseHeldDmpTask
          open={releaseDialogOpen}
          onClose={() => setReleaseDialogOpen(false)}
          onConfirm={handleConfirmRelease}
          taskUid={selectedTaskUid ?? ''}
        />
      </Paper>
    </Box>
  )
}
